import {DOCUMENT} from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {AuthService} from '@auth0/auth0-angular';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {switchMap} from 'rxjs';
import {AccessManagementService} from 'src/app/services/access-management.service';
import {environment} from 'src/environments/environment';
import {ISidebarData, sidebarDisplayData} from './sidebar-data';
import {RolesService} from 'src/app/services/roles.service';
import {ApprovalNotificationService} from 'src/app/services/approval-notification.service';
import {SignalRService} from 'src/app/services/signalr.service';

import {TooltipPosition} from 'src/app/enums/tooltip-position';
import {ToastrService} from 'ngx-toastr';

/**
 * The component represents the left blue panel, which displays the clickable icons, employee’s photo and logo Binariks
 */
@UntilDestroy()
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit {
  /**
   * isMainSidebar determines which sidebar will be shown: main (with 3 icons) or accountant (with 4 icons)
   */
  @Input() isMainSidebar!: boolean;
  @ViewChild('popup') popup: ElementRef | undefined;
  public collapsed = false;
  public timeTrackingUrl = environment.timeTrackingTool;
  public sidebarData!: ISidebarData[];
  public showPopup: boolean = false;
  public count: number = 0;
  public tooltipPositions = TooltipPosition;
  private readonly currentMonth = 1;

  constructor(
    private access: AccessManagementService,
    private approvalNotificationsService: ApprovalNotificationService,
    public auth: AuthService,
    private rolesService: RolesService,
    private signalRService: SignalRService,
    private notifier: ToastrService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.rolesService
      .updateLoggedUserRole()
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  ngOnInit() {
    this.sidebarData = sidebarDisplayData;
    this.sidebarData.forEach(el => {
      el.display = this.access.checkPermissions(el.allowedGroups);
    });
    this.approvalNotificationsService
      .countUnreadNotifications()
      .pipe(untilDestroyed(this))
      .subscribe(v => {
        if (v !== 0) {
          this.notifier.info('You have new notifications!');
        }
      });
    this.approvalNotificationsService.countOfNotifications
      .pipe(untilDestroyed(this))
      .subscribe(v => (this.count = v));
    this.signalRService
      .signalRObservable()
      .pipe(
        untilDestroyed(this),
        switchMap(v => {
          this.notifier.info(v);
          this.approvalNotificationsService
            .countUnreadNotifications()
            .subscribe();
          return this.approvalNotificationsService.getNotifications(
            this.currentMonth
          );
        })
      )
      .subscribe();
  }

  toggleCoppalse(event: MouseEvent): void {
    const target = (event.target as HTMLElement).classList;
    if (target.contains('sidebar')) {
      this.collapsed = !this.collapsed;
    }
  }

  @HostListener('document:click', ['$event'])
  clickedOut() {
    if (!this.popup?.nativeElement.contains(event?.target)) {
      this.showPopup = false;
    }
  }

  logout() {
    this.auth.logout({
      logoutParams: {
        returnTo: this.document.location.origin,
      },
    });
  }
}
